import { FiltersChips } from 'common/components/Filters/FiltersChips';
import {
  RSIM_INITIAL_FILTERS,
  RSimFilters,
  ValidRSimFiltersKeys,
} from 'src/rSimInventory/constants/rsim';
import {
  ACTIVE_PROFILE_FILTER,
  mapLastActiveProfileFilterTLabel,
  ActiveProfileFilter,
} from 'src/rSimInventory/constants/activeProfile';
import {
  LAST_UPDATE_TIME_FILTERS,
  LastUpdateTime,
  mapLastUpdateTimeToLabels,
} from 'src/rSimInventory/constants/lastUpdate';
import {
  SIMULATE_OUTAGE_FILTER,
  SimulateOutageFilter,
  mapSimulateOutageFilterToDisplayLabel,
} from 'src/rSimInventory/constants/simulateOutage';

import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { rsimFiltersAtom } from 'atoms/filters/rsim-products';
import { useResetAtom } from 'jotai/utils';
import { isEqual, omit } from 'lodash';
import { RSimTagsChips } from './RSimTagsChips';
import { RSimAccountChips } from './RSimAccountTags';
import { RSimProductCodesChips } from './RSimProductCodesChips';
import { createChipsContentText } from 'src/common/components/Chips/utils/chipsContentUtils';

import { RSimSubscriptionStatusChips } from './RSimSubscriptionStatusChips';
import { RSimMnosChips } from 'rSimInventory/Filters/Components/Chips/RSimMnosChips.tsx';

type RSimChipsFilters = Omit<RSimFilters, 'searchText' | 'chosenIdType' | 'mnoNames'>;

export const RSimFiltersChips = () => {
  const [filters, setFilters] = useAtom(rsimFiltersAtom);
  const clearAll = useResetAtom(rsimFiltersAtom);

  const onFilterClear = (filterName: ValidRSimFiltersKeys | 'eidRange') => {
    if (filterName === 'eidRange') {
      setFilters((currentFilters) => ({ ...currentFilters, eidFrom: '', eidTo: '' }));
      return;
    }

    setFilters((currentFilters) => {
      return { ...currentFilters, [filterName]: RSIM_INITIAL_FILTERS[filterName] };
    });
  };

  const { t } = useTranslation();
  const createValuesForChips = (
    filters: Omit<
      RSimChipsFilters,
      | 'tags'
      | 'accounts'
      | 'productCodes'
      | 'eidFrom'
      | 'eidTo'
      | 'subscriptionStatus'
      | 'mobileNetworkOperator'
      | 'mobileNetworkOperatorId'
    >,
  ) => {
    const filtersNames = {
      lastActiveProfile: 'Last active profile',
      lastUpdateTime: 'No update for',
      orderNumber: t('common.orderNumber'),
      connectionId: t('common.connectionId'),
      simulateOutage: t('common.simulateOutage'),
      mobileNetworkOperator: t('simInventory.mobileNetworkOperator'),
      eidRange: '',
    };

    const chipsValues: Partial<Record<keyof typeof filtersNames, string>> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'chosenIdType' || key === 'searchText' || key === 'mnoNames') {
        return;
      }

      if (
        (key === 'lastUpdateTime' && value === LAST_UPDATE_TIME_FILTERS.NONE) ||
        (key === 'lastActiveProfile' && value === ACTIVE_PROFILE_FILTER.ANY) ||
        (key === 'simulateOutage' && value === SIMULATE_OUTAGE_FILTER.ANY) ||
        (key === 'accounts' && isEqual(value, [])) ||
        (key === 'tags' && isEqual(value, [])) ||
        (key === 'mobileNetworkOperator' && isEqual(value, []))
      ) {
        return;
      }

      if (key === 'lastUpdateTime' && value !== LAST_UPDATE_TIME_FILTERS.NONE) {
        const valueText = mapLastUpdateTimeToLabels(value as LastUpdateTime);
        chipsValues[key as keyof typeof filtersNames] = createChipsContentText(
          filtersNames[key as keyof typeof filtersNames],
          valueText,
        );
      } else if (key === 'lastActiveProfile' && value !== ACTIVE_PROFILE_FILTER.ANY) {
        const valueText = mapLastActiveProfileFilterTLabel(value as ActiveProfileFilter);
        chipsValues[key as keyof typeof filtersNames] = createChipsContentText(
          filtersNames[key as keyof typeof filtersNames],
          valueText,
        );
      } else if (key === 'simulateOutage' && value !== SIMULATE_OUTAGE_FILTER.ANY) {
        const valueText = mapSimulateOutageFilterToDisplayLabel(value as SimulateOutageFilter);
        chipsValues[key as keyof typeof filtersNames] = createChipsContentText(
          filtersNames[key as keyof typeof filtersNames],
          `${valueText}`,
        );
      } else if (value !== '') {
        chipsValues[key as keyof typeof filtersNames] = createChipsContentText(
          filtersNames[key as keyof typeof filtersNames],
          value,
        );
      }
    });

    return chipsValues;
  };

  const otherFilters = omit(filters, [
    'tags',
    'accounts',
    'productCodes',
    'eidFrom',
    'eidTo',
    'subscriptionStatus',
    'mobileNetworkOperator',
    'mobileNetworkOperatorId',
  ]);
  const { eidFrom, eidTo } = filters;

  const filtersChips = createValuesForChips(otherFilters);

  if (eidFrom !== '' || eidTo !== '') {
    filtersChips.eidRange = `EID`;
    if (eidFrom) {
      filtersChips.eidRange += ` ${t('common.from')}: ${eidFrom}`;
    }

    if (eidTo) {
      filtersChips.eidRange += ` ${t('common.to')}: ${eidTo}`;
    }
  }

  return (
    <FiltersChips
      externalChips={
        <>
          <RSimSubscriptionStatusChips />
          <RSimTagsChips />
          <RSimAccountChips />
          <RSimProductCodesChips />
          <RSimMnosChips />
        </>
      }
      filters={filtersChips}
      onFilterClear={onFilterClear as (key: string) => void}
      onFiltersClear={clearAll}
    />
  );
};
